import { useEffect, useState } from "react";
import { func } from "prop-types";
import { useTranslation } from "react-i18next";
import { getSubscriptionPlansQuery, sendContactMessageQuery } from "@query";
import { Grid } from "@material-ui/core";
import { SubscriptionPlan } from "../SubscriptionPlan/SubscriptionPlan";
import { CustomPlan } from "../CustomPlan/CustomPlan";
import { PricingAutocomplete } from "../PricingAutocomplete/PricingAutocomplete";
import { COMPANY_SIZE_TIERS, ESTIMATE_TIERS, PACKAGE_FREE } from "@utils";
import { FeaturesList } from "../FeaturesList/FeaturesList";
import { PlanDurationSwitch } from "../PlanDurationSwitch/PlanDurationSwitch";
import useStyle from "./SubscriptionTable.style";
import { Alert, PageCircularProgress } from "@components";
import { useStores } from "@hooks";

export const SubscriptionsTable = ({ onSubSelect }) => {
  const { t } = useTranslation();
  const { userStore } = useStores();
  const classes = useStyle();

  const [isLoading, setLoadedState] = useState(true);
  const [pricingRequestSuccessVisible, showPricingRequestSuccess] = useState(false);

  const [plans, setPlans] = useState([]);

  const [duration, setDuration] = useState("year");
  const [users, setUsers] = useState(COMPANY_SIZE_TIERS[0]);
  const [estimates, setEstimates] = useState(ESTIMATE_TIERS[0]);

  const handlePlanSelect = (...args) => {
    onSubSelect(...args, users.amount, estimates.amount, duration);
  };
  
  const handlePriceRequest = async (...args) => {
    await sendContactMessageQuery(
      `New pricing request:\n\n`
      +`Company: ${userStore.data.company}\n`
      +`Name: ${userStore.data.fullname}\n`
      +`Email: ${userStore.data.email}\n\n`
      +`-------------------------------\n\n`
      + `Plan: ${args[1]}\n`
      + `Users: ${users.amount}\n`
      + `Estimates: ${estimates.amount}\n`
      + `Billing: ${t(`billing.per_${duration}`)}\n`
    );
    showPricingRequestSuccess(true);
  };

  const changeDuration = (val) => {
    setDuration(val);
  };

  const changeUsers = (e, val) => {
    setUsers(val);
  };

  const changeEstimates = (e, val) => {
    setEstimates(val);
  };

  const loadPlans = async () => {
    const p = await getSubscriptionPlansQuery();
    setPlans(p);
    setLoadedState(false);
  };

  useEffect(() => {
    loadPlans();
  }, []);

  if (isLoading)
    return <PageCircularProgress />;

  return (
    <Grid item container justifyContent="center" className="mb-8">
      <h1 className="text-5xl text-center">
        {t("views.subscription.plan.choose")}
      </h1>
      <Grid
        item
        container
        justifyContent="center"
        alignItems="center"
        wrap="nowrap"
        className={classes.settingsGrid}
      >
        <PricingAutocomplete
          id="usersSelect"
          value={users}
          options={COMPANY_SIZE_TIERS}
          onChange={changeUsers}
          label={t("views.subscription.plan.company_size")}
        />
        <PricingAutocomplete
          id="estimatesSelect"
          value={estimates}
          options={ESTIMATE_TIERS}
          onChange={changeEstimates}
          label={t("views.subscription.plan.estimates_month")}
        />
      </Grid>
      <PlanDurationSwitch value={duration} onChange={changeDuration} />
      <Grid
        item container
        spacing={6}
        className="mx-0"
        justifyContent="center"
      >
        <SubscriptionPlan
          code={ PACKAGE_FREE }
          name={t(`plans.${PACKAGE_FREE}`)}
          hashLink="features"
          duration={duration}
          userAmount={5}
          estimateAmount={3}
          basePrice={0}
          onSelect={handlePlanSelect}
        />
        {plans.map(({ code, name, best, prices }) => (
          <SubscriptionPlan
            key={code}
            code={code}
            name={name}
            hashLink="features"
            highlight={Boolean(best)}
            // onSelect={handlePlanSelect}
            onRequestPrice={handlePriceRequest}
            duration={duration}
            prices={prices}
            userAmount={users.amount}
            estimateAmount={estimates.amount}
          />
        ))}
        <CustomPlan />
      </Grid>

      <FeaturesList hashLink="features" />
      
      <Alert
        isOpen={pricingRequestSuccessVisible}
        title={t("views.subscription.plan.request_success.title")}
        onAccept={() => showPricingRequestSuccess(false)}
        acceptText={t("common.close")}
      >
        {t("views.subscription.plan.request_success.content")}
      </Alert>
    </Grid>
  );
};

SubscriptionsTable.propTypes = {
  onSubSelect: func.isRequired,
};
